<template>
    <div class="token-comparison">
        <!-- Token Selector Bar -->
        <div class="compass-card mb-4">
            <div class="selector-bar">
                <div class="selected-tokens">
                    <div
                        v-for="(token, idx) in selectedTokens"
                        :key="token.mint"
                        class="token-chip"
                        :style="{ borderColor: seriesColors[idx] }"
                    >
                        <span class="chip-color-dot" :style="{ background: seriesColors[idx] }"></span>
                        <img
                            v-if="token.logoURI"
                            :src="token.logoURI"
                            class="chip-logo"
                            :alt="token.symbol"
                            @error="e => e.target.style.display = 'none'"
                        >
                        <span class="chip-symbol">{{ token.symbol }}</span>
                        <button class="chip-remove" @click="removeToken(idx)" title="Remove">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                        </button>
                    </div>
                    <button
                        v-if="selectedTokens.length < 5"
                        class="add-token-btn"
                        @click="showSelector = true"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add Token
                    </button>
                </div>
                <div class="selector-controls">
                    <div class="time-range-selector">
                        <button
                            v-for="r in ranges"
                            :key="r.value"
                            class="range-btn"
                            :class="{ active: selectedRange === r.value }"
                            @click="selectRange(r.value)"
                        >{{ r.label }}</button>
                    </div>
                    <div class="time-range-selector">
                        <button
                            v-for="i in intervals"
                            :key="i.value"
                            class="range-btn"
                            :class="{ active: selectedInterval === i.value, disabled: isIntervalDisabled(i.value) }"
                            :disabled="isIntervalDisabled(i.value)"
                            :title="isIntervalDisabled(i.value) ? 'Hourly data unavailable for ranges over 30 days' : ''"
                            @click="selectInterval(i.value)"
                        >{{ i.label }}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="selectedTokens.length < 2" class="compass-card empty-state">
            <div class="empty-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--compass-purple)" stroke-width="1.5" opacity="0.4">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                <h5>Select at least 2 tokens to compare</h5>
                <p class="analytics-text-muted">Search and add tokens using the button above. You can compare up to 5 tokens side-by-side.</p>
                <button class="add-token-btn" style="font-size: 0.95rem; padding: 0.5rem 1.2rem;" @click="showSelector = true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="M21 21l-4.35-4.35"></path>
                    </svg>
                    Search Tokens
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="compass-card loading-state">
            <div class="spinner-border text-primary"></div>
            <span class="ms-3">Loading comparison data...</span>
        </div>

        <!-- Error State -->
        <div v-else-if="errorMessage" class="compass-card empty-state">
            <div class="empty-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--compass-pink, #e74c3c)" stroke-width="1.5" opacity="0.6">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <h5>{{ errorMessage }}</h5>
                <button class="add-token-btn" style="font-size: 0.95rem; padding: 0.5rem 1.2rem;" @click="fetchComparison()">
                    Retry
                </button>
            </div>
        </div>

        <!-- Comparison Content -->
        <template v-else-if="comparisonData">
            <!-- Summary Cards -->
            <div class="row mb-4">
                <div
                    v-for="(token, idx) in selectedTokens"
                    :key="'card-' + token.mint"
                    class="col-12 col-md-6 col-xl mb-3"
                >
                    <div class="compass-stat-card" :style="{ borderTop: '3px solid ' + seriesColors[idx] }">
                        <div class="card-header-row">
                            <div class="token-identity">
                                <img
                                    v-if="token.logoURI"
                                    :src="token.logoURI"
                                    class="card-token-logo"
                                    :alt="token.symbol"
                                    @error="e => e.target.style.display = 'none'"
                                >
                                <div>
                                    <div class="token-symbol-label">{{ token.symbol }}</div>
                                    <div class="token-name-label analytics-text-subtle">{{ token.name }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="stat-value price-display">
                            {{ formatPrice(getTokenPrice(token.mint)) }}
                        </div>
                        <div :class="['pct-change', getTokenChange(token.mint) >= 0 ? 'pct-positive' : 'pct-negative']" style="margin-bottom: 0.75rem;">
                            {{ getTokenChange(token.mint) >= 0 ? '+' : '' }}{{ getTokenChange(token.mint)?.toFixed(2) }}% (24h)
                        </div>
                        <div class="card-metrics">
                            <div class="metric-row">
                                <span class="metric-label analytics-text-muted">Volume 24h</span>
                                <span class="metric-value mono-num">
                                    {{ formatCompact(getTokenSnapshot(token.mint)?.volume_usd_24h) }}
                                    <span v-if="getTokenSnapshot(token.mint)?.volume_change_pct != null"
                                        :class="['change-badge', getTokenSnapshot(token.mint).volume_change_pct >= 0 ? 'pct-positive' : 'pct-negative']">
                                        {{ formatChangePct(getTokenSnapshot(token.mint).volume_change_pct) }}
                                    </span>
                                </span>
                            </div>
                            <div class="metric-row">
                                <span class="metric-label analytics-text-muted">Traders 24h</span>
                                <span class="metric-value mono-num">
                                    {{ formatNumber(getTokenSnapshot(token.mint)?.traders_24h) }}
                                    <span v-if="getTokenSnapshot(token.mint)?.traders_change_pct != null"
                                        :class="['change-badge', getTokenSnapshot(token.mint).traders_change_pct >= 0 ? 'pct-positive' : 'pct-negative']">
                                        {{ formatChangePct(getTokenSnapshot(token.mint).traders_change_pct) }}
                                    </span>
                                </span>
                            </div>
                            <div class="metric-row">
                                <span class="metric-label analytics-text-muted">Market Cap</span>
                                <span class="metric-value mono-num">{{ formatCompact(getTokenPrice(token.mint, 'market_cap_usd')) }}</span>
                            </div>
                            <div class="metric-row">
                                <span class="metric-label analytics-text-muted">Buy/Sell</span>
                                <span class="metric-value mono-num">{{ formatBuySell(getTokenSnapshot(token.mint)?.buy_sell_ratio) }}</span>
                            </div>
                            <div class="metric-row">
                                <span class="metric-label analytics-text-muted">Holders</span>
                                <span class="metric-value mono-num">{{ formatNumber(getTokenData(token.mint, 'holder_count')) }}</span>
                            </div>
                            <div v-if="getTokenData(token.mint, 'liquidity_usd')" class="metric-row">
                                <span class="metric-label analytics-text-muted">Liquidity</span>
                                <span class="metric-value mono-num">{{ formatCompact(getTokenData(token.mint, 'liquidity_usd')) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Chart Mode Toggle -->
            <div class="chart-mode-bar mb-3">
                <div class="analytics-tabs">
                    <button
                        class="tab-btn"
                        :class="{ active: chartMode === 'normalized' }"
                        @click="chartMode = 'normalized'"
                    >% Change</button>
                    <button
                        class="tab-btn"
                        :class="{ active: chartMode === 'absolute' }"
                        @click="chartMode = 'absolute'"
                    >Absolute</button>
                </div>
            </div>

            <!-- Price Chart -->
            <div class="analytics-panel mb-4">
                <div class="panel-header">
                    <div class="panel-title">
                        {{ chartMode === 'normalized' ? 'Normalised Price Performance (%)' : 'Price (USD)' }}
                    </div>
                </div>
                <div class="panel-body">
                    <div class="chart-container" style="height: 400px;">
                        <canvas ref="priceCanvas"></canvas>
                    </div>
                </div>
            </div>

            <!-- Volume Chart -->
            <div class="analytics-panel mb-4">
                <div class="panel-header">
                    <div class="panel-title">Volume (USD)</div>
                </div>
                <div class="panel-body">
                    <div class="chart-container" style="height: 300px;">
                        <canvas ref="volumeCanvas"></canvas>
                    </div>
                </div>
            </div>

            <!-- Traders Chart -->
            <div v-if="hasTraderData" class="analytics-panel mb-4">
                <div class="panel-header">
                    <div class="panel-title">Unique Traders</div>
                </div>
                <div class="panel-body">
                    <div class="chart-container" style="height: 300px;">
                        <canvas ref="tradersCanvas"></canvas>
                    </div>
                </div>
            </div>

            <!-- Holder Growth Chart -->
            <div v-if="hasHolderTrends" class="analytics-panel mb-4">
                <div class="panel-header">
                    <div class="panel-title">Holder Growth</div>
                </div>
                <div class="panel-body">
                    <div class="chart-container" style="height: 300px;">
                        <canvas ref="holdersCanvas"></canvas>
                    </div>
                </div>
            </div>

            <!-- Metrics Comparison Table -->
            <div class="analytics-panel mb-4">
                <div class="panel-header">
                    <div class="panel-title">Metrics Comparison</div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive table-mobile-scroll">
                        <table class="table compass-table">
                            <thead>
                                <tr>
                                    <th>Metric</th>
                                    <th
                                        v-for="(token, idx) in selectedTokens"
                                        :key="'th-' + token.mint"
                                        class="text-end"
                                    >
                                        <span class="th-token" :style="{ color: seriesColors[idx] }">{{ token.symbol }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Price</td>
                                    <td v-for="token in selectedTokens" :key="'price-' + token.mint" class="text-end td-num">
                                        {{ formatPrice(getTokenPrice(token.mint)) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>24h Change</td>
                                    <td v-for="token in selectedTokens" :key="'change-' + token.mint" class="text-end td-num">
                                        <span :class="['pct-change', getTokenChange(token.mint) >= 0 ? 'pct-positive' : 'pct-negative']">
                                            {{ getTokenChange(token.mint) >= 0 ? '+' : '' }}{{ getTokenChange(token.mint)?.toFixed(2) }}%
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Market Cap</td>
                                    <td v-for="token in selectedTokens" :key="'mcap-' + token.mint" class="text-end td-num">
                                        {{ formatCompact(getTokenPrice(token.mint, 'market_cap_usd')) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>24h Volume</td>
                                    <td v-for="token in selectedTokens" :key="'vol-' + token.mint" class="text-end td-num">
                                        {{ formatCompact(getTokenSnapshot(token.mint)?.volume_usd_24h) }}
                                        <span v-if="getTokenSnapshot(token.mint)?.volume_change_pct != null"
                                            :class="['change-badge', getTokenSnapshot(token.mint).volume_change_pct >= 0 ? 'pct-positive' : 'pct-negative']">
                                            {{ formatChangePct(getTokenSnapshot(token.mint).volume_change_pct) }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>24h Trades</td>
                                    <td v-for="token in selectedTokens" :key="'trades-' + token.mint" class="text-end td-num">
                                        {{ formatNumber(getTokenSnapshot(token.mint)?.trade_count_24h) }}
                                        <span v-if="getTokenSnapshot(token.mint)?.trades_change_pct != null"
                                            :class="['change-badge', getTokenSnapshot(token.mint).trades_change_pct >= 0 ? 'pct-positive' : 'pct-negative']">
                                            {{ formatChangePct(getTokenSnapshot(token.mint).trades_change_pct) }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>24h Traders</td>
                                    <td v-for="token in selectedTokens" :key="'traders-' + token.mint" class="text-end td-num">
                                        {{ formatNumber(getTokenSnapshot(token.mint)?.traders_24h) }}
                                        <span v-if="getTokenSnapshot(token.mint)?.traders_change_pct != null"
                                            :class="['change-badge', getTokenSnapshot(token.mint).traders_change_pct >= 0 ? 'pct-positive' : 'pct-negative']">
                                            {{ formatChangePct(getTokenSnapshot(token.mint).traders_change_pct) }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Buy Volume</td>
                                    <td v-for="token in selectedTokens" :key="'buy-' + token.mint" class="text-end td-num">
                                        {{ formatCompact(getTokenSnapshot(token.mint)?.buy_volume_24h) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Sell Volume</td>
                                    <td v-for="token in selectedTokens" :key="'sell-' + token.mint" class="text-end td-num">
                                        {{ formatCompact(getTokenSnapshot(token.mint)?.sell_volume_24h) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Buy/Sell Ratio</td>
                                    <td v-for="token in selectedTokens" :key="'ratio-' + token.mint" class="text-end td-num">
                                        {{ formatBuySell(getTokenSnapshot(token.mint)?.buy_sell_ratio) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Avg Trade Size</td>
                                    <td v-for="token in selectedTokens" :key="'avgts-' + token.mint" class="text-end td-num">
                                        {{ formatAvgTradeSize(token.mint) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Holders</td>
                                    <td v-for="token in selectedTokens" :key="'holders-' + token.mint" class="text-end td-num">
                                        {{ formatNumber(getTokenData(token.mint, 'holder_count')) }}
                                    </td>
                                </tr>
                                <tr v-if="anyHasLiquidity">
                                    <td>Liquidity</td>
                                    <td v-for="token in selectedTokens" :key="'liq-' + token.mint" class="text-end td-num">
                                        {{ formatCompact(getTokenData(token.mint, 'liquidity_usd')) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <!-- Token Selector Modal -->
        <token-selector-modal
            v-model="showSelector"
            mode="from"
            :current-from-token="null"
            :current-to-token="null"
            @select="onTokenSelected"
        />
    </div>
</template>

<script>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { colors, getLineChartOptions, getBarChartOptions, getThemeAwareColors } from '../composables/useChartConfig';

export default {
    name: 'TokenComparison',

    setup() {
        const selectedTokens = ref([]);
        const comparisonData = ref(null);
        const loading = ref(false);
        const errorMessage = ref(null);
        const showSelector = ref(false);
        const selectedRange = ref('30d');
        const selectedInterval = ref('1d');
        const chartMode = ref('normalized');
        const priceCanvas = ref(null);
        const volumeCanvas = ref(null);
        const tradersCanvas = ref(null);
        const holdersCanvas = ref(null);
        let priceChart = null;
        let volumeChart = null;
        let tradersChart = null;
        let holdersChart = null;

        const seriesColors = colors.series.slice(0, 5);

        const ranges = [
            { label: '7D', value: '7d' },
            { label: '14D', value: '14d' },
            { label: '30D', value: '30d' },
            { label: '90D', value: '90d' },
            { label: '1Y', value: '365d' },
        ];

        const intervals = [
            { label: '1H', value: '1h' },
            { label: '1D', value: '1d' },
        ];

        // --- URL state management ---
        const loadFromUrl = async () => {
            const params = new URLSearchParams(window.location.search);
            if (params.get('range')) selectedRange.value = params.get('range');
            if (params.get('interval')) selectedInterval.value = params.get('interval');
            // Auto-correct invalid range+interval combo from URL
            if (rangeExceedsHourlyBudget(selectedRange.value) && selectedInterval.value === '1h') {
                selectedInterval.value = '1d';
            }
            const tokensParam = params.get('tokens');
            if (tokensParam) {
                const mints = tokensParam.split(',').filter(Boolean).slice(0, 5);
                if (mints.length >= 1) {
                    const resolved = await Promise.all(
                        mints.map(async (mint) => {
                            try {
                                const res = await axios.get('/api/tokens/search', { params: { q: mint, limit: 1, enrich: true } });
                                return res.data?.[0] || null;
                            } catch (e) {
                                console.error('Failed to resolve token:', mint, e);
                                return null;
                            }
                        })
                    );
                    const unique = resolved.filter((t, i, arr) => t && !arr.slice(0, i).find(prev => prev?.mint === t.mint));
                    selectedTokens.value = unique;
                }
            }
        };

        const updateUrl = () => {
            const mints = selectedTokens.value.map(t => t.mint).join(',');
            const params = new URLSearchParams();
            if (mints) params.set('tokens', mints);
            if (selectedRange.value !== '30d') params.set('range', selectedRange.value);
            if (selectedInterval.value !== '1d') params.set('interval', selectedInterval.value);
            const qs = params.toString();
            const url = window.location.pathname + (qs ? '?' + qs : '');
            window.history.replaceState({}, '', url);
        };

        // --- Token management ---
        const onTokenSelected = ({ token }) => {
            if (selectedTokens.value.length >= 5) return;
            if (selectedTokens.value.find(t => t.mint === token.mint)) return;
            selectedTokens.value.push(token);
            updateUrl();
        };

        const removeToken = (idx) => {
            selectedTokens.value.splice(idx, 1);
            updateUrl();
        };

        // Ranges that exceed the 1H candle budget (>30 days)
        const hourlyMaxRange = '30d';
        const rangeExceedsHourlyBudget = (range) => {
            const days = parseInt(range);
            return days > 30;
        };

        const selectRange = (range) => {
            selectedRange.value = range;
            // Auto-switch to 1D when range is too large for hourly candles
            if (rangeExceedsHourlyBudget(range) && selectedInterval.value === '1h') {
                selectedInterval.value = '1d';
            }
            updateUrl();
        };

        const selectInterval = (interval) => {
            // Prevent selecting 1H for ranges that would truncate
            if (interval === '1h' && rangeExceedsHourlyBudget(selectedRange.value)) {
                return;
            }
            selectedInterval.value = interval;
            updateUrl();
        };

        const isIntervalDisabled = (interval) => {
            return interval === '1h' && rangeExceedsHourlyBudget(selectedRange.value);
        };

        // --- Data fetching ---
        const fetchComparison = async () => {
            if (selectedTokens.value.length < 2) {
                comparisonData.value = null;
                return;
            }

            loading.value = true;
            errorMessage.value = null;
            try {
                const mints = selectedTokens.value.map(t => t.mint).join(',');
                const res = await axios.get('/api/v1/compare/tokens', {
                    params: {
                        mints,
                        metric: 'price',
                        range: selectedRange.value,
                        interval: selectedInterval.value,
                    }
                });
                comparisonData.value = res.data?.data || res.data;
                await nextTick();
                renderCharts();
            } catch (e) {
                console.error('Comparison fetch failed:', e);
                comparisonData.value = null;
                errorMessage.value = 'Failed to load comparison data. Please try again.';
            } finally {
                loading.value = false;
            }
        };

        // --- Computed helpers ---
        const hasTraderData = ref(false);
        const hasHolderTrends = ref(false);
        const anyHasLiquidity = ref(false);

        const updateDataFlags = () => {
            if (!comparisonData.value?.tokens) {
                hasTraderData.value = false;
                hasHolderTrends.value = false;
                anyHasLiquidity.value = false;
                return;
            }
            const tokens = comparisonData.value.tokens;
            hasTraderData.value = selectedTokens.value.some(
                t => (tokens[t.mint]?.trader_series || []).length > 0
            );
            hasHolderTrends.value = selectedTokens.value.some(
                t => (tokens[t.mint]?.holder_trends || []).length > 0
            );
            anyHasLiquidity.value = selectedTokens.value.some(
                t => tokens[t.mint]?.liquidity_usd > 0
            );
        };

        // --- Chart rendering ---
        const destroyCharts = () => {
            if (priceChart) { priceChart.destroy(); priceChart = null; }
            if (volumeChart) { volumeChart.destroy(); volumeChart = null; }
            if (tradersChart) { tradersChart.destroy(); tradersChart = null; }
            if (holdersChart) { holdersChart.destroy(); holdersChart = null; }
        };

        const renderCharts = () => {
            destroyCharts();
            if (!comparisonData.value?.tokens) return;
            updateDataFlags();
            renderPriceChart();
            renderVolumeChart();
            if (hasTraderData.value) nextTick(() => renderTradersChart());
            if (hasHolderTrends.value) nextTick(() => renderHoldersChart());
        };

        const renderPriceChart = () => {
            const canvas = priceCanvas.value;
            if (!canvas) return;

            const tokens = comparisonData.value.tokens;
            // Build unified label set from the first token that has data
            const refMint = selectedTokens.value.find(t => (tokens[t.mint]?.time_series || []).length > 0)?.mint;
            if (!refMint) return;
            const labels = tokens[refMint].time_series.map(p => p.label);

            const datasets = [];
            selectedTokens.value.forEach((token, idx) => {
                const series = tokens[token.mint]?.time_series || [];
                if (!series.length) return;

                const basePrice = series[0]?.close || series[0]?.open || 1;

                datasets.push({
                    label: token.symbol,
                    data: series.map(p =>
                        chartMode.value === 'normalized'
                            ? ((p.close - basePrice) / basePrice) * 100
                            : p.close
                    ),
                    borderColor: seriesColors[idx],
                    backgroundColor: seriesColors[idx] + '20',
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    tension: 0.3,
                    fill: false,
                });
            });

            const lineOpts = getLineChartOptions({
                useDynamicColors: true,
                fill: false,
                formatCallback: (val) => chartMode.value === 'normalized'
                    ? val.toFixed(1) + '%'
                    : '$' + formatCompactRaw(val),
            });

            // Allow negative values in normalized mode
            if (chartMode.value === 'normalized') {
                lineOpts.scales.y.min = undefined;
            }

            lineOpts.plugins.tooltip = {
                ...lineOpts.plugins.tooltip,
                callbacks: {
                    label: (ctx) => {
                        const val = ctx.parsed.y;
                        if (chartMode.value === 'normalized') {
                            return `${ctx.dataset.label}: ${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
                        }
                        return `${ctx.dataset.label}: $${formatPriceRaw(val)}`;
                    }
                }
            };

            lineOpts.plugins.legend = {
                display: true,
                position: 'top',
                labels: {
                    color: getThemeAwareColors().textMuted,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: { family: "'JetBrains Mono', monospace" }
                },
            };

            lineOpts.scales.x = {
                ...lineOpts.scales.x,
                ticks: { ...lineOpts.scales.x.ticks, maxTicksLimit: 12 },
            };

            priceChart = new Chart(canvas, {
                type: 'line',
                data: { labels, datasets },
                options: lineOpts,
            });
        };

        const renderVolumeChart = () => {
            const canvas = volumeCanvas.value;
            if (!canvas) return;

            const tokens = comparisonData.value.tokens;
            const refMint = selectedTokens.value.find(t => (tokens[t.mint]?.time_series || []).length > 0)?.mint;
            if (!refMint) return;
            const labels = tokens[refMint].time_series.map(p => p.label);

            const datasets = [];
            selectedTokens.value.forEach((token, idx) => {
                const series = tokens[token.mint]?.time_series || [];
                if (!series.length) return;

                datasets.push({
                    label: token.symbol,
                    data: series.map(p => p.volume || 0),
                    backgroundColor: seriesColors[idx] + '80',
                    borderColor: seriesColors[idx],
                    borderWidth: 1,
                    barPercentage: 0.8,
                    categoryPercentage: 0.9,
                });
            });

            const barOpts = getBarChartOptions({
                showLegend: true,
                useDynamicColors: true,
                formatCallback: (val) => '$' + formatCompactRaw(val),
            });

            barOpts.plugins.legend = {
                display: true,
                position: 'top',
                labels: {
                    color: getThemeAwareColors().textMuted,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: { family: "'JetBrains Mono', monospace" }
                },
            };

            barOpts.plugins.tooltip = {
                ...barOpts.plugins.tooltip,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: $${formatCompactRaw(ctx.parsed.y)}`,
                }
            };

            barOpts.scales.x = {
                ...barOpts.scales.x,
                ticks: { ...barOpts.scales.x?.ticks, maxTicksLimit: 12 },
            };

            volumeChart = new Chart(canvas, {
                type: 'bar',
                data: { labels, datasets },
                options: barOpts,
            });
        };

        const renderTradersChart = () => {
            const canvas = tradersCanvas.value;
            if (!canvas) return;

            const tokens = comparisonData.value.tokens;
            const refMint = selectedTokens.value.find(t => (tokens[t.mint]?.trader_series || []).length > 0)?.mint;
            if (!refMint) return;
            const labels = tokens[refMint].trader_series.map(p => p.label);

            const datasets = [];
            selectedTokens.value.forEach((token, idx) => {
                const series = tokens[token.mint]?.trader_series || [];
                if (!series.length) return;

                datasets.push({
                    label: token.symbol,
                    data: series.map(p => p.traders),
                    borderColor: seriesColors[idx],
                    backgroundColor: seriesColors[idx] + '20',
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    tension: 0.3,
                    fill: false,
                });
            });

            if (!datasets.length) return;

            const lineOpts = getLineChartOptions({
                useDynamicColors: true,
                fill: false,
                formatCallback: (val) => formatCompactRaw(val),
            });

            lineOpts.plugins.legend = {
                display: true,
                position: 'top',
                labels: {
                    color: getThemeAwareColors().textMuted,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: { family: "'JetBrains Mono', monospace" }
                },
            };

            lineOpts.plugins.tooltip = {
                ...lineOpts.plugins.tooltip,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString('en-US')} traders`,
                }
            };

            lineOpts.scales.x = {
                ...lineOpts.scales.x,
                ticks: { ...lineOpts.scales.x.ticks, maxTicksLimit: 12 },
            };

            tradersChart = new Chart(canvas, {
                type: 'line',
                data: { labels, datasets },
                options: lineOpts,
            });
        };

        const renderHoldersChart = () => {
            const canvas = holdersCanvas.value;
            if (!canvas) return;

            const tokens = comparisonData.value.tokens;
            const refMint = selectedTokens.value.find(t => (tokens[t.mint]?.holder_trends || []).length > 0)?.mint;
            if (!refMint) return;
            const labels = tokens[refMint].holder_trends.map(p => p.label || p.timestamp?.slice(0, 10));

            const datasets = [];
            selectedTokens.value.forEach((token, idx) => {
                const series = tokens[token.mint]?.holder_trends || [];
                if (!series.length) return;

                datasets.push({
                    label: token.symbol,
                    data: series.map(p => p.holder_count),
                    borderColor: seriesColors[idx],
                    backgroundColor: seriesColors[idx] + '20',
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    tension: 0.3,
                    fill: false,
                });
            });

            if (!datasets.length) return;

            const lineOpts = getLineChartOptions({
                useDynamicColors: true,
                fill: false,
                formatCallback: (val) => formatCompactRaw(val),
            });

            lineOpts.plugins.legend = {
                display: true,
                position: 'top',
                labels: {
                    color: getThemeAwareColors().textMuted,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: { family: "'JetBrains Mono', monospace" }
                },
            };

            lineOpts.plugins.tooltip = {
                ...lineOpts.plugins.tooltip,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString('en-US')} holders`,
                }
            };

            lineOpts.scales.x = {
                ...lineOpts.scales.x,
                ticks: { ...lineOpts.scales.x.ticks, maxTicksLimit: 12 },
            };

            holdersChart = new Chart(canvas, {
                type: 'line',
                data: { labels, datasets },
                options: lineOpts,
            });
        };

        // --- Formatters ---
        const formatPriceRaw = (price) => {
            if (!price || price === 0) return '0.00';
            if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            if (price >= 0.01) return price.toFixed(4);
            if (price >= 0.000001) return price.toPrecision(3);
            return price.toPrecision(2);
        };

        const formatPrice = (price) => {
            if (price === null || price === undefined) return '-';
            return '$' + formatPriceRaw(price);
        };

        const formatCompactRaw = (val) => {
            if (!val || val === 0) return '0';
            if (val >= 1e9) return (val / 1e9).toFixed(2) + 'B';
            if (val >= 1e6) return (val / 1e6).toFixed(2) + 'M';
            if (val >= 1e3) return (val / 1e3).toFixed(1) + 'K';
            return val.toFixed(2);
        };

        const formatCompact = (val) => {
            if (val === null || val === undefined) return '-';
            return '$' + formatCompactRaw(val);
        };

        const formatNumber = (val) => {
            if (val === null || val === undefined) return '-';
            return Number(val).toLocaleString('en-US');
        };

        const formatBuySell = (ratio) => {
            if (ratio === null || ratio === undefined) return '-';
            return (ratio * 100).toFixed(1) + '% buy';
        };

        const formatChangePct = (pct) => {
            if (pct === null || pct === undefined) return '';
            const sign = pct >= 0 ? '+' : '';
            return sign + pct.toFixed(1) + '%';
        };

        // --- Accessors ---
        const getTokenPrice = (mint, field = 'price_usd') => {
            return comparisonData.value?.tokens?.[mint]?.price?.[field] ?? null;
        };

        const getTokenChange = (mint) => {
            return comparisonData.value?.tokens?.[mint]?.price?.change_24h_percent ?? 0;
        };

        const getTokenSnapshot = (mint) => {
            return comparisonData.value?.tokens?.[mint]?.snapshot_24h ?? null;
        };

        const getTokenData = (mint, field) => {
            return comparisonData.value?.tokens?.[mint]?.[field] ?? null;
        };

        const formatAvgTradeSize = (mint) => {
            const snap = getTokenSnapshot(mint);
            if (!snap || !snap.trade_count_24h) return '-';
            const avg = snap.volume_usd_24h / snap.trade_count_24h;
            return '$' + formatCompactRaw(avg);
        };

        // --- Watchers ---
        watch(
            () => [selectedTokens.value.length, selectedRange.value, selectedInterval.value],
            () => { fetchComparison(); },
            { deep: true }
        );

        watch(chartMode, () => {
            if (comparisonData.value) {
                nextTick(() => renderCharts());
            }
        });

        loadFromUrl();

        onBeforeUnmount(() => {
            destroyCharts();
        });

        return {
            selectedTokens,
            comparisonData,
            loading,
            errorMessage,
            showSelector,
            selectedRange,
            selectedInterval,
            chartMode,
            seriesColors,
            ranges,
            intervals,
            priceCanvas,
            volumeCanvas,
            tradersCanvas,
            holdersCanvas,
            hasTraderData,
            hasHolderTrends,
            anyHasLiquidity,
            fetchComparison,
            onTokenSelected,
            removeToken,
            selectRange,
            selectInterval,
            isIntervalDisabled,
            formatPrice,
            formatCompact,
            formatNumber,
            formatBuySell,
            formatChangePct,
            formatAvgTradeSize,
            getTokenPrice,
            getTokenChange,
            getTokenSnapshot,
            getTokenData,
        };
    }
};
</script>

<style scoped>
/* Selector Bar */
.selector-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
}

.selected-tokens {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

.token-chip {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.6rem 0.35rem 0.5rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--compass-border);
    border-radius: 20px;
    font-size: 0.85rem;
}

.chip-color-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.chip-logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
}

.chip-symbol {
    font-weight: 600;
}

.chip-remove {
    background: none;
    border: none;
    color: var(--compass-neutral, #6b7280);
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    line-height: 1;
    margin-left: 2px;
}

.chip-remove:hover {
    color: var(--compass-danger);
}

.add-token-btn {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    background: rgba(139, 92, 246, 0.15);
    border: 1px dashed var(--compass-border);
    border-radius: 20px;
    color: var(--compass-purple);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.add-token-btn:hover {
    background: rgba(139, 92, 246, 0.25);
    border-color: var(--compass-purple);
}

.selector-controls {
    display: flex;
    gap: 0.75rem;
    align-items: center;
}

.range-btn.disabled,
.range-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
}

/* Empty State */
.empty-state {
    padding: 4rem 2rem;
}

.empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
}

.empty-content h5 {
    margin: 0;
}

/* Loading */
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4rem;
}

/* Summary Cards */
.card-header-row {
    margin-bottom: 0.75rem;
}

.token-identity {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.card-token-logo {
    width: 32px;
    height: 32px;
    border-radius: 50%;
}

.token-symbol-label {
    font-weight: 700;
    font-size: 0.95rem;
}

.token-name-label {
    font-size: 0.7rem;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.card-metrics {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.metric-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.8rem;
}

/* Change badges */
.change-badge {
    font-size: 0.7rem;
    font-weight: 500;
    margin-left: 4px;
}

/* Chart Mode Bar */
.chart-mode-bar {
    display: flex;
    align-items: center;
}

/* Charts */
.chart-container {
    position: relative;
}

/* Table */
.th-token {
    font-weight: 700;
}

/* Responsive */
@media (max-width: 768px) {
    .selector-bar {
        flex-direction: column;
        align-items: flex-start;
    }

    .selector-controls {
        flex-wrap: wrap;
    }

    .chart-container {
        height: 250px !important;
    }
}
</style>
